import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useActiveLeagues } from "@/hooks/useActiveLeagues";

const LAST_LEAGUE_KEY = "mt:lastLeague";
export const DEFAULT_LEAGUE = "ENG_PL";

/**
 * Keeps a piece of view state in the URL so browser back/forward restores it.
 * Defaults are omitted from the URL to keep links clean.
 */
export function useUrlParamState<T extends string>(
  key: string,
  defaultValue: T,
  options?: { validate?: (value: string) => boolean }
): [T, (value: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const validate = options?.validate;

  const raw = searchParams.get(key);
  const value =
    raw !== null && (!validate || validate(raw)) ? (raw as T) : defaultValue;

  const setValue = useCallback(
    (next: T) => {
      const params = new URLSearchParams(window.location.search);
      if (next === defaultValue || next === "" || next == null) {
        params.delete(key);
      } else {
        params.set(key, next);
      }
      setSearchParams(params, { replace: true });
    },
    [key, defaultValue, setSearchParams]
  );

  return [value, setValue];
}

/** Boolean flavour of useUrlParamState ("1" / absent). */
export function useUrlBoolState(
  key: string,
  defaultValue = false
): [boolean, (value: boolean) => void] {
  const [raw, setRaw] = useUrlParamState(key, defaultValue ? "1" : "0");
  const set = useCallback((next: boolean) => setRaw(next ? "1" : "0"), [setRaw]);
  return [raw === "1", set];
}

function readLastLeague(): string | null {
  try {
    return sessionStorage.getItem(LAST_LEAGUE_KEY);
  } catch {
    return null;
  }
}

function writeLastLeague(code: string) {
  try {
    sessionStorage.setItem(LAST_LEAGUE_KEY, code);
  } catch {
    /* storage unavailable — ignore */
  }
}

/**
 * League selection that lives in the URL (so back/forward works) and is
 * remembered for the browsing session (so site navigation back to a page
 * without a league param restores the last choice).
 */
export function useStickyLeague(): [string, (code: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: activeLeagues = [] } = useActiveLeagues();

  const isKnown = useCallback(
    (code: string) =>
      activeLeagues.length === 0 || activeLeagues.some((l) => l.code === code),
    [activeLeagues]
  );

  const param = searchParams.get("league");

  const league = useMemo(() => {
    if (param && isKnown(param)) return param;
    const remembered = readLastLeague();
    if (remembered && isKnown(remembered)) return remembered;
    return DEFAULT_LEAGUE;
  }, [param, isKnown]);

  const setLeague = useCallback(
    (code: string) => {
      writeLastLeague(code);
      const params = new URLSearchParams(window.location.search);
      if (code === DEFAULT_LEAGUE) {
        params.delete("league");
      } else {
        params.set("league", code);
      }
      setSearchParams(params, { replace: true });
    },
    [setSearchParams]
  );

  return [league, setLeague];
}
